// src/pages/DashboardPrincipal.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Building2, Users2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import ModalMultiEmpresa from "../Components/ModalMultiEmpresa";
import { useEmpresa } from "../context/EmpresaContext";
import { api } from "../api";

const aniversariantes = [
  {
    nome: "Carlos Silva",
    data: "13/04",
    foto: "https://i.pravatar.cc/100?img=12",
  },
  {
    nome: "Mariana Lima",
    data: "22/04",
    foto: "https://i.pravatar.cc/100?img=30",
  },
  {
    nome: "João Pedro",
    data: "29/04",
    foto: "https://i.pravatar.cc/100?img=45",
  },
];

const DEFAULT_USER_IMAGE = "/default-user.png";
const API_STORAGE_FALLBACK = "https://api.captacrm.com.br";

function getApiStorageBaseUrl() {
  const envBase =
    import.meta.env?.VITE_API_STORAGE_URL ||
    import.meta.env?.VITE_API_URL ||
    import.meta.env?.VITE_API_BASE_URL ||
    import.meta.env?.VITE_BACKEND_URL ||
    "";

  const axiosBase = api?.defaults?.baseURL || "";
  const base = String(envBase || axiosBase || "").trim();

  if (!base) return API_STORAGE_FALLBACK;

  // Quando o axios usa proxy relativo, ex: "/api", isso NÃO serve /storage.
  // Nesse caso precisamos apontar direto para o domínio público da API.
  if (
    base === "/api" ||
    base === "api" ||
    base.startsWith("/api/") ||
    base.startsWith("./") ||
    base.startsWith("../")
  ) {
    return API_STORAGE_FALLBACK;
  }

  if (base.startsWith("http://") || base.startsWith("https://")) {
    return base.replace(/\/api\/?$/, "").replace(/\/$/, "");
  }

  // Qualquer base relativa não serve para /storage quando front e API são domínios diferentes.
  if (base.startsWith("/")) return API_STORAGE_FALLBACK;

  return API_STORAGE_FALLBACK;
}

function resolverUrlImagem(url) {
  const value = String(url || "").trim();

  if (!value) return DEFAULT_USER_IMAGE;

  if (value.startsWith("data:image/")) return value;
  if (value.startsWith("blob:")) return value;
  if (value.startsWith("http://") || value.startsWith("https://")) return value;

  if (value.startsWith("/storage/") || value.startsWith("/uploads/")) {
    return `${getApiStorageBaseUrl()}${value}`;
  }

  if (value.startsWith("storage/") || value.startsWith("uploads/")) {
    return `${getApiStorageBaseUrl()}/${value}`;
  }

  if (value.startsWith("/")) return value;

  return `${getApiStorageBaseUrl()}/${value}`;
}

function AvatarUsuario({
  foto,
  nome,
  sizeClass = "w-12 h-12 sm:w-16 sm:h-16",
  borderClass = "border-white",
  className = "",
  online = false,
}) {
  const [erroImagem, setErroImagem] = useState(false);
  const src = erroImagem ? DEFAULT_USER_IMAGE : resolverUrlImagem(foto);

  return (
    <div className="relative shrink-0">
      <img
        src={src}
        alt={nome || "Usuário"}
        onError={() => setErroImagem(true)}
        className={`${sizeClass} rounded-full border-2 ${borderClass} bg-white object-cover object-center shadow-md transition-all duration-300 ${className}`}
        loading="lazy"
        decoding="async"
      />

      {online && (
        <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white bg-green-500 animate-pulse shadow" />
      )}
    </div>
  );
}

function lerUsuarioLocalStorage() {
  try {
    const usuarioLogado = JSON.parse(
      localStorage.getItem("usuarioLogado") || "{}",
    );
    const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");

    return {
      ...usuario,
      ...usuarioLogado,
      foto: usuarioLogado?.foto || usuario?.foto,
    };
  } catch {
    return {};
  }
}

function lerEmpresasLocalStorage() {
  try {
    const arr = JSON.parse(localStorage.getItem("empresas") || "[]");
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

export default function DashboardPrincipal() {
  const navigate = useNavigate();
  const { usuario, logout } = useAuth();
  const { empresaAtiva, empresas, trocarEmpresa, salvarEmpresas } =
    useEmpresa();

  const [modalEmpresaAberto, setModalEmpresaAberto] = useState(false);
  const [usuariosOnline, setUsuariosOnline] = useState([]);
  const [loadingUsuariosOnline, setLoadingUsuariosOnline] = useState(false);
  const [usuarioLocalAtualizado, setUsuarioLocalAtualizado] = useState(() =>
    lerUsuarioLocalStorage(),
  );

  const empresasLs = useMemo(() => lerEmpresasLocalStorage(), []);

  useEffect(() => {
    const atualizarUsuarioLocal = () => {
      setUsuarioLocalAtualizado(lerUsuarioLocalStorage());
    };

    atualizarUsuarioLocal();

    window.addEventListener("storage", atualizarUsuarioLocal);

    return () => {
      window.removeEventListener("storage", atualizarUsuarioLocal);
    };
  }, []);

  const usuarioEfetivo = useMemo(() => {
    return {
      ...usuarioLocalAtualizado,
      ...(usuario || {}),
      foto: usuario?.foto || usuarioLocalAtualizado?.foto,
      nome: usuario?.nome || usuarioLocalAtualizado?.nome,
      cargo: usuario?.cargo || usuarioLocalAtualizado?.cargo,
      acessos: usuario?.acessos ?? usuarioLocalAtualizado?.acessos,
      empresa_id: usuario?.empresa_id ?? usuarioLocalAtualizado?.empresa_id,
      empresas: usuario?.empresas?.length
        ? usuario.empresas
        : usuarioLocalAtualizado?.empresas,
    };
  }, [usuario, usuarioLocalAtualizado]);

  const empresasDisponiveis = useMemo(() => {
    const listaContexto = Array.isArray(empresas) ? empresas : [];
    const listaUsuario = Array.isArray(usuarioEfetivo?.empresas)
      ? usuarioEfetivo.empresas
      : [];
    const listaEmpresasLs = Array.isArray(empresasLs) ? empresasLs : [];

    if (listaContexto.length > 0) return listaContexto;
    if (listaUsuario.length > 0) return listaUsuario;
    if (listaEmpresasLs.length > 0) return listaEmpresasLs;

    return [];
  }, [empresas, usuarioEfetivo, empresasLs]);

  const empresaAtivaEfetiva = useMemo(() => {
    if (empresaAtiva?.id) return empresaAtiva;

    if (empresasDisponiveis.length > 0) {
      return (
        empresasDisponiveis.find(
          (emp) =>
            String(emp.id ?? emp.empresa_id) ===
            String(usuarioEfetivo?.empresa_id),
        ) || empresasDisponiveis[0]
      );
    }

    if (usuarioEfetivo?.empresa_id) {
      return { id: usuarioEfetivo.empresa_id };
    }

    return null;
  }, [empresaAtiva, empresasDisponiveis, usuarioEfetivo]);

  const empresasPadronizadas = useMemo(() => {
    return empresasDisponiveis.map((e) => ({
      id: e.id ?? e.empresa_id,
      nome_fantasia: e.nome_fantasia ?? e.nome,
      logo_url: e.logo_url ?? undefined,
      nome: e.nome ?? e.nome_fantasia,
    }));
  }, [empresasDisponiveis]);

  useEffect(() => {
    if (!usuarioEfetivo?.id) return;

    async function buscarEmpresas() {
      try {
        const res = await api.get("/empresas");
        const data = Array.isArray(res?.data) ? res.data : [];

        if (data.length > 0) {
          salvarEmpresas(data);
        }
      } catch (err) {
        console.error("❌ Erro ao buscar empresas:", err);
      }
    }

    buscarEmpresas();
  }, [usuarioEfetivo?.id, salvarEmpresas]);

  useEffect(() => {
    if (!empresaAtiva?.id && empresaAtivaEfetiva?.id) {
      trocarEmpresa(empresaAtivaEfetiva);
    }
  }, [empresaAtiva?.id, empresaAtivaEfetiva, trocarEmpresa]);

  useEffect(() => {
    if (!empresaAtivaEfetiva?.id) {
      setUsuariosOnline([]);
      setLoadingUsuariosOnline(false);
      return;
    }

    setLoadingUsuariosOnline(true);

    async function fetchUsuariosOnline() {
      try {
        const res = await api.get(`/usuarios-online/${empresaAtivaEfetiva.id}`);
        const data = Array.isArray(res?.data) ? res.data : [];
        setUsuariosOnline(data);
      } catch (err) {
        console.error("❌ Erro ao buscar usuários online:", err);
        setUsuariosOnline([]);
      } finally {
        setLoadingUsuariosOnline(false);
      }
    }

    fetchUsuariosOnline();
  }, [empresaAtivaEfetiva?.id]);

  function handleTrocaEmpresa(empresaSelecionada) {
    trocarEmpresa(empresaSelecionada);
    setModalEmpresaAberto(false);
    setUsuariosOnline([]);
  }

  if (!usuarioEfetivo?.id) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 to-blue-700 text-white pb-10">
      <div className="flex flex-col sm:flex-row justify-center sm:justify-end items-center gap-3 w-full px-2 sm:px-8 pt-6">
        <div className="flex items-center gap-3 bg-blue-900/80 rounded-xl shadow-lg px-3 sm:px-4 py-2 w-full sm:w-auto">
          <AvatarUsuario
            foto={usuarioEfetivo?.foto}
            nome={usuarioEfetivo?.nome}
            sizeClass="w-12 h-12 sm:w-16 sm:h-16"
            borderClass="border-white"
            online
          />

          <div className="flex flex-col items-start sm:items-end flex-1 min-w-0">
            <span className="font-semibold text-base text-white truncate">
              {usuarioEfetivo?.nome}
            </span>

            <span className="text-xs text-blue-200 truncate">
              {empresaAtivaEfetiva?.nome_fantasia ||
                empresaAtivaEfetiva?.nome ||
                ""}
            </span>

            <span className="text-xs text-blue-300 truncate">
              {usuarioEfetivo?.cargo}
            </span>
          </div>

          <button
            onClick={() => {
              if (empresasPadronizadas.length === 0) {
                alert("Nenhuma empresa disponível para troca.");
                return;
              }

              setModalEmpresaAberto(true);
            }}
            title="Trocar de empresa"
            className="flex items-center gap-1 bg-blue-800 hover:bg-blue-700 text-white px-2 py-1 rounded-lg shadow text-xs sm:text-base"
          >
            <Building2 size={18} /> Trocar
          </button>

          <button
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
            title="Sair"
            className="flex items-center gap-1 bg-blue-800 hover:bg-blue-700 text-white px-2 py-1 rounded-lg shadow text-xs sm:text-base"
          >
            <LogOut size={16} /> Sair
          </button>
        </div>

        {modalEmpresaAberto && (
          <ModalMultiEmpresa
            isOpen
            onClose={() => setModalEmpresaAberto(false)}
            empresas={empresasPadronizadas}
            empresaAtivaId={empresaAtivaEfetiva?.id}
            onSelectEmpresa={handleTrocaEmpresa}
          />
        )}
      </div>

      <div className="max-w-2xl w-full mx-auto mt-4 px-2 sm:px-0">
        <h1 className="text-xl sm:text-3xl font-bold mb-2 text-center sm:text-left">
          Bem-vindo, {usuarioEfetivo?.nome?.split(" ")[0] || ""}
        </h1>

        <p className="text-blue-200 mb-3 text-center sm:text-left text-sm sm:text-base">
          Este é o seu acesso número {usuarioEfetivo?.acessos ?? "--"} ao
          sistema.
        </p>

        {empresaAtivaEfetiva?.nome_fantasia || empresaAtivaEfetiva?.nome ? (
          <div className="flex items-center gap-2 mb-2 justify-center sm:justify-start">
            <Building2 size={20} className="text-blue-100" />
            <span className="text-white text-base font-semibold">
              {empresaAtivaEfetiva?.nome_fantasia || empresaAtivaEfetiva?.nome}
            </span>
          </div>
        ) : (
          <p className="text-blue-300 mb-2">⏳ Carregando empresa...</p>
        )}

        <div className="flex items-center gap-2 mb-2 mt-6 justify-center sm:justify-start">
          <Users2 size={22} className="text-green-200" />
          <h2 className="text-lg sm:text-2xl font-semibold text-green-200">
            Usuários Online
          </h2>
        </div>

        {loadingUsuariosOnline ? (
          <p className="text-blue-200 text-center sm:text-left">
            Carregando usuários online...
          </p>
        ) : usuariosOnline.length > 0 ? (
          <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4 mb-6">
            {usuariosOnline.map((u) => (
              <div
                key={u.id}
                className="bg-white text-blue-900 rounded-xl p-2 sm:p-3 shadow flex items-center gap-2 sm:gap-3"
              >
                <AvatarUsuario
                  foto={u.foto}
                  nome={u.nome}
                  sizeClass="w-10 h-10 sm:w-12 sm:h-12"
                  borderClass="border-green-500"
                  online
                />

                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-xs sm:text-base truncate">
                    {u.nome}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {u.email || "-"}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-blue-200 mb-6 text-center sm:text-left">
            Nenhum usuário online.
          </p>
        )}

        <h2 className="text-lg sm:text-2xl font-semibold mb-4 flex items-center gap-2 justify-center sm:justify-start">
          🎂 Aniversariantes do mês
        </h2>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4 mb-10">
          {aniversariantes.map((a, index) => (
            <div
              key={index}
              className="bg-white text-blue-800 rounded-xl p-2 sm:p-4 shadow flex flex-col items-center"
            >
              <AvatarUsuario
                foto={a.foto}
                nome={a.nome}
                sizeClass="w-14 h-14 sm:w-20 sm:h-20"
                borderClass="border-blue-300"
                className="mb-2"
              />

              <p className="font-bold text-center text-xs sm:text-sm">
                🎉 {a.nome}
              </p>

              <span className="text-xs text-blue-500">{a.data}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
